export type FilePickerSource = 'filepicker' | 'takephoto' | 'takeaudio' | 'takevideo';

export type CaptureSource = Exclude<FilePickerSource, 'filepicker'>;

export type CaptureSupport = {
  camera: boolean;
  recorder: boolean;
};

export const DEFAULT_SOURCES: FilePickerSource[] = [
  'filepicker',
  'takephoto',
  'takeaudio',
  'takevideo',
];

const CAPTURES: Array<{ source: CaptureSource; type: string; needsRecorder: boolean }> = [
  { source: 'takephoto', type: 'image/', needsRecorder: false },
  { source: 'takevideo', type: 'video/', needsRecorder: true },
  { source: 'takeaudio', type: 'audio/', needsRecorder: true },
];

function acceptPrefixes(accept?: string) {
  const prefixes = (accept || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.includes('/'))
    .map((s) => (s.endsWith('/*') ? s.slice(0, -1) : s));
  return prefixes.length ? prefixes : undefined;
}

export function isAcceptedFile(file: File, accept?: string) {
  const prefixes = acceptPrefixes(accept);
  if (!prefixes) {
    return true;
  }
  const type = file.type.toLowerCase();
  return prefixes.some((prefix) =>
    prefix.endsWith('/') ? type.startsWith(prefix) : type === prefix,
  );
}

export function availableCaptures(
  sources: FilePickerSource[],
  accept: string | undefined,
  support: CaptureSupport,
): CaptureSource[] {
  const prefixes = acceptPrefixes(accept);
  return CAPTURES.filter(({ source, type, needsRecorder }) => {
    if (!sources.includes(source) || !support.camera || (needsRecorder && !support.recorder)) {
      return false;
    }
    return !prefixes || prefixes.some((prefix) => prefix.startsWith(type));
  }).map(({ source }) => source);
}

export function isEditableImage(file: Pick<File, 'type'>) {
  const type = file.type.toLowerCase();
  return type.startsWith('image/') && type !== 'image/svg+xml';
}
